<template lang="pug">
  .picture-container(
    :id="id"
    :class="[{ error, loading, loaded, skeleton: showPlaceholder }, `object-fit-${objectFit}`]"
    ref="container"
  )
    figure(ref="figure" :key="image.id")
      picture.background(v-if="!(initialized && loading) && ['scale-down', 'contain'].includes(objectFit) && (original.src.type !== 'image/png' || dontShowMatchingBackgroundForPng === false)" :style="pictureStyle")
        template(v-if="showMatchingBackground && ! background.error")
          source(
            :srcset="original.webp.loading"
            :width="original.width"
            :height="original.height"
            :type="original.webp.type"
          )
          source(
            :srcset="original.src.loading"
            :width="original.width"
            :height="original.height"
            :type="original.src.type"
          )
          img(
            :src="original.src.loading"
            :title="title"
            :alt="alt"
            :width="original.width"
            :height="original.height"
            :style="{ 'min-width': `${original.width}px`, 'min-height': `${original.height}px` }"
            @load="background.loaded = true"
            @error="background.error = true"
          )
      picture.original(v-if="initialized" ref="picture" :style="pictureStyle")
        source(
          v-for="(source, key) in sources" :key="key"
          v-if="source.srcset"
          v-bind="lazy && loaded !== true ? { 'data-srcset': source.srcset } : { srcset: source.srcset }"
          :sizes="source.size"
          :type="source.type"
          :width="source.width"
          :height="source.height"
        )
        img(
          v-bind="lazy && loaded !== true ? { 'data-src': original.src.regular } : { src: original.src.regular }"
          :title="title"
          :alt="alt"
          :width="original.width"
          :height="original.height"
          :style="imageStyle"
          :class="{ lazy }"
          @load="handleLoaded"
          @error="handleError"
        )
      .info(v-if="error")
        slot(name="error")
          svg.exclamation-triangle(v-if="missingIcon === 'exclamation-triangle'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6")
            path(stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z")
          svg.x-mark(v-if="missingIcon === 'x-mark'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6")
            path(stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" )
          svg.large-cross(v-if="missingIcon === 'large-cross'")
            line(x1="0" y1="100%" x2="100%" y2="0")
            line(x1="0" y1="0" x2="100%" y2="100%")
          template(v-if="hideMissingInfo === false")
            p {{ alt || title || 'Bild' }}
            small(v-if="missingInfo") {{ missingInfo }}
      transition(name="fade" :duration="150")
        picture.preview(v-if="initialized && loading" :style="pictureStyle")
          Spinner(
            v-if="showSpinner"
            :size="spinnerSize"
            :thickness="spinnerThickness"
          )
          template(v-if="showPreview && ! preview.error")
            source(
              :srcset="original.webp.loading"
              :width="original.width"
              :height="original.height"
              :type="original.webp.type"
            )
            source(
              :srcset="original.src.loading"
              :width="original.width"
              :height="original.height"
              :type="original.src.type"
            )
            img(
              :src="original.src.loading"
              :title="title"
              :alt="alt"
              :width="original.width"
              :height="original.height"
              :style="{ 'min-width': `${original.width}px`, 'min-height': `${original.height}px` }"
              @load="preview.loaded = true"
              @error="preview.error = true"
            )
      slot
</template>

<script>
import uniqid from 'uniqid';
import { get, trim, debounce } from '@/helpers';
import findGeneratedFile from '@/mixins/findGeneratedFile';

import LazyLoad from 'vanilla-lazyload';

export default {
  name: 'Picture',
  mixins: [findGeneratedFile],
  props: {
    image: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    alt: {
      type: String,
      required: false
    },
    // width: {
    //   type: Number,
    //   required: false
    // },
    maxWidth: {
      type: Number,
      required: false
    },
    // height: {
    //   type: Number,
    //   required: false
    // },
    maxHeight: {
      type: Number,
      required: false
    },
    missingInfo: {
      type: String,
      default: 'konnte nicht gefunden werden'
    },
    hideMissingInfo: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    debug: {
      type: Boolean,
      default: false
    },
    optimize: {
      // if speed is selected, get only prerendered variants if possible
      type: String,
      default: 'traffic',
      validator: val => ['speed', 'traffic'].includes(val)
    },
    fit: {
      type: String,
      default: 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1'
    },
    missingIcon: {
      type: [String, Boolean],
      default: 'x-mark',
      validator: val => ['x-mark', 'exclamation-triangle', 'large-cross', null, false, undefined].includes(val)
    },
    objectFit: {
      type: String,
      default: 'scale-down',
      validator: val => ['contain', 'cover', 'fill', 'none', 'scale-down'].includes(val)
    },
    showPreview: {
      type: Boolean,
      default: true
    },
    showMatchingBackground: {
      type: Boolean,
      default: false
    },
    dontShowMatchingBackgroundForPng: {
      type: Boolean,
      default: true
    },
    showPlaceholder: {
      type: Boolean,
      default: true
    },
    showSpinner: {
      type: Boolean,
      default: false
    },
    spinnerSize: {
      type: Number,
      default: 6
    },
    spinnerThickness: {
      type: Number,
      default: 2
    },
    ignoreContainerPadding: {
      type: Boolean,
      default: false
    },
    ignoreContainerWidth: {
      type: Boolean,
      default: false
    },
    ignoreContainerHeight: {
      type: Boolean,
      default: false
    }
    // forceWidth: {
    //   type: Boolean,
    //   default: false
    // },
    // forceHeight: {
    //   type: Boolean,
    //   default: false
    // },
  },
  data: () => ({
    id: undefined,
    initialized: false,
    loading: true,
    loaded: false,
    error: false,
    preview: {
      loading: false,
      loaded: false,
      error: false
    },
    background: {
      loading: false,
      loaded: false,
      error: false
    },
    container: {
      observer: undefined,
      width: undefined,
      height: undefined
    },
    lazyload: undefined
  }),
  computed: {
    original() {
      let fit = get(this.image, 'pivot.fit') || this.fit;
      // const crop = get(this.image, 'pivot.crop');
      // const method = get(this.image, 'pivot.method');

      let w = get(this.image, 'pivot.w');
      let h = get(this.image, 'pivot.h');

      let x = get(this.image, 'pivot.x', 0.5);
      let y = get(this.image, 'pivot.y', 0.5);
      let z = get(this.image, 'pivot.z') || 1;

      let width, height, ratio;

      width = this.image.width / z;
      height = this.image.height / z;
      ratio = width / height;

      let maxWidth = this.maxWidth;
      if (this.container.width > 0) {
        if (!maxWidth) maxWidth = this.container.width;
        else maxWidth = Math.min(maxWidth, this.container.width);
      } else if (this.container.width < 0 && maxWidth) {
        maxWidth += this.container.width;
      }

      let maxHeight = this.maxHeight;
      if (this.container.height > 0) {
        if (!maxHeight) maxHeight = this.container.height;
        else maxHeight = Math.min(maxHeight, this.container.height);
      } else if (this.container.height < 0 && maxHeight) {
        maxHeight += this.container.height;
      }

      const calculateParameters = (w, h, z = null) => {
        const ratio = w / h;

        if (maxWidth > 0 && maxWidth < w) {
          w = maxWidth;
          h = w / ratio;
        }
        if (maxHeight > 0 && maxHeight < h) {
          h = maxHeight;
          w = h * ratio;
        }

        const width = w;
        const height = h;

        switch (this.objectFit) {
          case 'cover':
            // if (this.debug) console.log('container', this.container.width, this.container.height)
            if (this.container.width && this.container.height) {
              const desiredRatio = this.container.width / this.container.height;
              if (desiredRatio > ratio) {
                // max width
                w = this.container.width;
                h = this.container.width / desiredRatio;
              } else {
                // max height
                w = this.container.height * desiredRatio;
                h = this.container.height;
              }

              if (z !== 1) {
                // if original aspect ratio is larger than the requested
                // then z (zoom) must be calculated by height
                if (this.image.width / this.image.height > desiredRatio) {
                  z = this.image.height / height;
                }

                // if original aspect ratio is smaller than the requested
                // then z (zoom) must be calculated by width
                else if (this.image.width / this.image.height < desiredRatio) {
                  z = this.image.width / width;
                }

                // if original aspect ratio is equal to the requested
                // then z (zoom) can be calculated by width or height
                else {
                  z = this.image.width / width;
                }
              }
            }
            break;

          // TODO: implement other objectFit options

          case 'contain':
          default:
            // clip
            if (maxWidth > 0 && maxWidth < w) {
              w = maxWidth;
              h = w / ratio;
            }
            if (maxHeight > 0 && maxHeight < h) {
              h = maxHeight;
              w = h * ratio;
            }
            break;
        }

        return { width: w, height: h, z };
      };

      if (w && h) {
        // user defined crop

        ratio = w / h;

        const parameters = calculateParameters(w, h, z);
        w = width = parameters.width;
        h = height = parameters.height;
        z = parameters.z;

        fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`);
        fit = fit.replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);

        if (!get(this.image, 'pivot.z')) fit += `&z=${z}`;
        else fit = fit.replace(/z=([0-9]+\.?[0-9]*)/, `z=${z}`);
      } else {
        // default crop

        const parameters = calculateParameters(width, height);
        width = parameters.width;
        height = parameters.height;

        if (!w) fit += `&w=${width}`;
        else fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`);
        w = width;

        if (!h) fit += `&h=${height}`;
        else fit = fit.replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);
        h = height;

        // if (this.debug) console.log('fit', fit)
      }

      const mime = get(this.image, 'source.mime') || get(this.image, 'mime');
      return {
        src: {
          type: mime,
          regular: this.findGeneratedFile(this.image, fit, this.optimize),
          loading: this.findGeneratedFile(this.image, `${fit}&preview=blur`, this.optimize)
        },
        webp: {
          type: 'image/webp',
          regular: this.findGeneratedFile(this.image, `${fit}&format=webp`, this.optimize),
          loading: this.findGeneratedFile(this.image, `${fit}&format=webp&preview=blur`, this.optimize)
        },
        width,
        height,
        pivot: { fit, w, h, x, y, z }
      };
    },
    sources() {
      const mime = get(this.image, 'source.mime') || get(this.image, 'mime');
      const processables = ['image/jpeg', 'image/gif', 'image/png'];
      if (!processables.includes(mime)) return;

      let sources = {
        webp: {
          src: [],
          type: 'image/webp',
          width: this.original.width,
          height: this.original.height,
          srcset: ''
        },
        regular: {
          src: [],
          type: mime,
          width: this.original.width,
          height: this.original.height,
          srcset: ''
        }
      };

      for (let dpr = 1; dpr <= 4; dpr += 0.5) {
        let fit = this.original.pivot.fit;

        let width = this.original.width;
        let height = this.original.height;

        if (dpr > 1) {
          // check if the image can be requested
          // with the current device pixel ratio
          if (this.image.width / this.original.pivot.z < width * dpr) continue;
          if (this.image.height / this.original.pivot.z < height * dpr) continue;

          width *= dpr;
          height *= dpr;

          fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);
        }

        const webp = this.findGeneratedFile(this.image, `${fit}&format=webp`, this.optimize);
        // let webpIndex = sources.webp.src.indexOf(`${webp} ${dpr}x`)
        // if (webpIndex >= 0) sources.webp.src.splice(webpIndex, 1);
        sources.webp.src.push(`${webp} ${dpr}x`);

        const regular = this.findGeneratedFile(this.image, fit, this.optimize);
        // let regularIndex = sources.regular.src.indexOf(`${regular} ${dpr}x`)
        // if (regularIndex >= 0) sources.regular.src.splice(regularIndex, 1);
        sources.regular.src.push(`${regular} ${dpr}x`);
      }

      if (sources.webp.src.length) sources.webp.srcset = sources.webp.src.join(', ');
      if (sources.regular.src.length) sources.regular.srcset = sources.regular.src.join(', ');

      return sources;
    },
    pictureStyle() {
      let style;
      // if (this.loading || this.error) style = { width: `${this.original.width}px`, height: `${this.original.height}px` }
      // else if (this.error) style = { height: `${this.original.height}px` }
      if (!this.loaded) {
        style = {
          display: 'block',
          width: `${this.original.width}px`,
          height: `${this.original.height}px`
        };
      }
      return style;
    },
    imageStyle() {
      let style;
      // const w = this.forceWidth ? 'width' : 'max-width'
      // const h = this.forceHeight ? 'height' : 'max-height'
      // style = {
      //   [w]: `${this.original.width}px`,
      //   [h]: `${this.original.height}px`
      // }
      return style;
    }
  },
  methods: {
    // normalize fit query string
    updateContainerDimansions() {
      if (this.ignoreContainerWidth && this.ignoreContainerHeight) return;

      if (this.$refs.container) {
        // const rect = this.$refs.container.getBoundingClientRect();
        const style = getComputedStyle(this.$refs.container);

        if (!this.ignoreContainerWidth || this.objectFit === 'cover') {
          this.container.width = this.$refs.container.clientWidth;
          if (!this.ignoreContainerPadding) {
            this.container.width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
          }
        }

        if (!this.ignoreContainerHeight || this.objectFit === 'cover') {
          this.container.height = this.$refs.container.clientHeight;
          if (!this.ignoreContainerPadding) {
            this.container.height -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
          }
        }
      }

      // if (this.debug) console.log('update container', this.container.width, this.container.height)
    },
    updateLazyLoad() {
      if (this.lazyload) this.lazyload.update();
    },
    handleLoaded() {
      // console.log('loaded', event)
      this.loading = false;
      this.loaded = true;
      this.error = false;
      this.$emit('loaded');
    },
    handleError() {
      // console.log('error', event)
      this.loading = false;
      this.loaded = false;
      this.error = true;
      this.$emit('error');
    },
    handleResize() {
      // if (this.debug) console.log('handleResize');
      this.updateContainerDimansions();
    },
    handleScroll() {
      // console.log('handleScroll');
    }
  },
  mounted() {
    this.id = `picture-${uniqid()}`;

    window.addEventListener('scroll', this.handleScroll);
    // window.addEventListener('resize', this.handleResize)
    if (this.$refs.container) {
      this.container.observer = new ResizeObserver(debounce(this.handleResize, 300));
      this.container.observer.observe(this.$refs.container);

      if (this.lazy) {
        this.lazyload = new LazyLoad({
          container: this.$refs.container.parentNode.closest('[lazy-scroll-container]') || document,
          elements_selector: `#${this.id} img.lazy`,
          use_native: true,
          threshold: 0,
          callback_loaded: this.handleLoaded,
          callback_error: this.handleError
        });
      }
    }

    this.$nextTick(() => {
      this.updateContainerDimansions();
      this.initialized = true;

      this.$nextTick(this.updateLazyLoad);
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    // window.removeEventListener('resize', this.handleResize)
    if (this.container.observer) this.container.observer.disconnect();
    if (this.lazyload) this.lazyload.destroy();
  }
};
</script>

<style lang="sass" scoped>
.picture-container
  figure
    position: relative
  &.error
    .info
      color: theme('colors.gray.400')
      font-size: 0.8rem
      position: absolute
      left: 0
      top: 0
      width: 100%
      height: 100%
      display: flex
      flex-direction: column
      justify-content: center
      align-items: center
      text-align: center
    img
      visibility: hidden
      display: none
  &.loading
    picture.original img
      // visibility: hidden
      // opacity: 0
  &.skeleton
    picture
      background-color: theme('colors.gray.200')
    &.loaded
      picture
        background-color: initial
  &.object-fit
    &-scale-down
      picture
        &.preview
          // width: 100%
          // height: 100%
          img
            object-fit: cover
        img
          object-fit: scale-down
          width: 100%
          height: 100%
    &-contain
      picture
        &.preview
          // width: 100%
          // height: 100%
          img
            object-fit: cover
        img
          object-fit: contain
          width: 100%
          height: 100%
    &-cover
      figure
        position: absolute
        left: 0
        right: 0
        width: 100%
        height: 100%
      picture
        img
          object-fit: cover
          width: 100%
          height: 100%
        &.preview
          // width: 100% !important
          // height: 100% !important
          img
            width: 100% !important
            height: 100% !important
  &::v-deep .spinner
    border-color: theme('colors.gray.300')
    border-top-color: var(--color-primary)
  picture
    // display: block
    position: relative
    // &.original img
    //   transition: opacity .15s ease
    // &.preview,
    // &.original
    //   z-index: 1
    &.preview,
    &.background
      position: absolute
      left: 0
      top: 0
      width: 100% !important
      height: 100% !important
    &.background
      // z-index: 0
      img
        object-fit: cover
  .spinner-container
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
  img:not([src]):not([srcset])
    visibility: hidden
  svg.large-cross
    position: absolute
    width: calc(100% - 1rem)
    height: calc(100% - 1rem)
    top: 0.5rem
    left: 0.5rem
    stroke: currentColor

.fade-enter-active
  // transition: opacity .15s ease
  transition: none
.fade-leave-active
  transition: opacity .15s cubic-bezier(1.0, 0.5, 0.8, 1.0)
.fade-enter, .fade-leave-to
  opacity: 0
</style>
